import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CloudArrowUp,
  Brain,
  CheckCircle,
  XCircle,
  Clock,
  ArrowClockwise,
  PlugsConnected,
  TestTube,
  SpinnerGap,
  Warning
} from "@phosphor-icons/react";

export default function IntegrationPage() {
  const [aiLogs, setAiLogs] = useState([]);
  const [zohoLogs, setZohoLogs] = useState([]);
  const [zohoStatus, setZohoStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testingZoho, setTestingZoho] = useState(false);
  const [retryingLog, setRetryingLog] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [aiRes, zohoRes, statusRes] = await Promise.all([
        axios.get(`${API}/integration/ai-logs?limit=50`),
        axios.get(`${API}/integration/zoho-logs?limit=50`),
        axios.get(`${API}/integration/zoho-status`)
      ]);
      
      setAiLogs(aiRes.data.logs || []);
      setZohoLogs(zohoRes.data.logs || []);
      setZohoStatus(statusRes.data.status || null);
    } catch (error) {
      console.error("Failed to fetch integration data:", error);
      toast.error("Failed to load integration data");
    } finally {
      setLoading(false);
    }
  };

  const handleTestZoho = async () => {
    setTestingZoho(true);
    try {
      const response = await axios.post(`${API}/integration/test-zoho`);
      if (response.data.success) {
        toast.success("Zoho connection successful!");
      } else {
        toast.error(response.data.message || "Zoho connection failed");
      }
      fetchAllData();
    } catch (error) {
      toast.error("Failed to test Zoho connection");
    } finally {
      setTestingZoho(false);
    }
  };

  const handleRetrySync = async (logId) => {
    setRetryingLog(logId);
    try {
      const response = await axios.post(`${API}/integration/retry-sync/${logId}`);
      if (response.data.success) {
        toast.success("Retry initiated");
        fetchAllData();
      } else {
        toast.error("Retry failed");
      }
    } catch (error) {
      toast.error("Failed to retry sync");
    } finally {
      setRetryingLog(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "success":
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
            <CheckCircle size={12} weight="fill" /> Success
          </span>
        );
      case "failed":
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
            <XCircle size={12} weight="fill" /> Failed
          </span>
        );
      case "processing":
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
            <Clock size={12} weight="fill" /> Processing
          </span>
        );
      case "simulated":
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">
            <Warning size={12} weight="fill" /> Simulated
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
            {status || "Unknown"}
          </span>
        );
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  // Stats calculation
  const aiStats = {
    total: aiLogs.length,
    success: aiLogs.filter(l => l.status?.toLowerCase() === "success").length,
    failed: aiLogs.filter(l => l.status?.toLowerCase() === "failed").length
  };

  const zohoStats = {
    total: zohoLogs.length,
    success: zohoLogs.filter(l => l.status?.toLowerCase() === "success").length,
    failed: zohoLogs.filter(l => l.status?.toLowerCase() === "failed").length
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <div className="animate-pulse text-gray-500">Loading integration center...</div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="integration-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integration Center</h1>
          <p className="text-gray-500">Monitor AI extractions and Zoho Creator sync status</p>
        </div>
        <Button onClick={fetchAllData} variant="outline">
          <ArrowClockwise size={18} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Zoho Connection Status */}
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  zohoStatus?.connected ? "bg-green-100" : "bg-red-100"
                }`}>
                  <PlugsConnected 
                    size={20} 
                    className={zohoStatus?.connected ? "text-green-600" : "text-red-600"} 
                    weight="duotone" 
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Zoho Creator</p>
                  <p className={`text-xs ${zohoStatus?.connected ? "text-green-600" : "text-red-600"}`}>
                    {zohoStatus?.configured ? (zohoStatus?.connected ? "Connected" : "Configured") : "Not Configured"}
                  </p>
                </div>
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={handleTestZoho}
                disabled={testingZoho || !zohoStatus?.configured}
                data-testid="test-zoho-btn"
              >
                {testingZoho ? <SpinnerGap size={14} className="animate-spin" /> : "Test"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* AI Extractions */}
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Brain size={20} className="text-purple-600" weight="duotone" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{aiStats.total}</p>
                <p className="text-xs text-gray-500">AI Extractions</p>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs">
              <span className="text-green-600">{aiStats.success} success</span>
              <span className="text-red-600">{aiStats.failed} failed</span>
            </div>
          </CardContent>
        </Card>

        {/* Zoho Syncs */}
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <CloudArrowUp size={20} className="text-blue-600" weight="duotone" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{zohoStats.total}</p>
                <p className="text-xs text-gray-500">Zoho Syncs</p>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs">
              <span className="text-green-600">{zohoStats.success} success</span>
              <span className="text-red-600">{zohoStats.failed} failed</span>
            </div>
          </CardContent>
        </Card>

        {/* Last Sync */}
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <Clock size={20} className="text-teal-600" weight="duotone" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Last Sync</p>
                <p className="text-xs text-gray-500">
                  {zohoStatus?.lastSyncTime 
                    ? formatDate(zohoStatus.lastSyncTime)
                    : "No syncs yet"
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logs Tabs */}
      <Tabs defaultValue="ai" className="space-y-4">
        <TabsList className="bg-white border">
          <TabsTrigger value="ai" className="data-[state=active]:bg-[#7c5cfc] data-[state=active]:text-white">
            <Brain size={16} className="mr-2" />
            AI Extraction Logs
          </TabsTrigger>
          <TabsTrigger value="zoho" className="data-[state=active]:bg-[#7c5cfc] data-[state=active]:text-white">
            <CloudArrowUp size={16} className="mr-2" />
            Zoho Sync Logs
          </TabsTrigger>
        </TabsList>

        {/* AI Logs Tab */}
        <TabsContent value="ai">
          <Card className="bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">AI Extraction History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {aiLogs.length === 0 ? (
                <div className="py-12 text-center text-gray-400">
                  <Brain size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No AI extractions yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs font-medium text-gray-500">ID</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500">Trial No</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500">Files</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500">Status</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500">Timestamp</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aiLogs.map((log) => (
                      <TableRow key={log.id} className="hover:bg-gray-50" data-testid={`ai-log-${log.id}`}>
                        <TableCell className="text-xs text-gray-500 font-mono">
                          {log.id?.slice(0, 8)}...
                        </TableCell>
                        <TableCell className="text-sm font-medium text-gray-900">
                          {log.trialNo || "-"}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {log.fileCount || 0} files
                        </TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {formatDate(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          {log.status?.toLowerCase() === "failed" && (
                            <Button variant="ghost" size="sm" className="text-xs text-blue-600">
                              <ArrowClockwise size={14} className="mr-1" />
                              Reprocess
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Zoho Logs Tab */}
        <TabsContent value="zoho">
          <Card className="bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">Zoho Creator Sync History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {zohoLogs.length === 0 ? (
                <div className="py-12 text-center text-gray-400">
                  <CloudArrowUp size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No Zoho syncs yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs font-medium text-gray-500">ID</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500">Sample No</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500">Trial No</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500">Record ID</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500">Status</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500">Timestamp</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {zohoLogs.map((log) => (
                      <TableRow key={log.id} className="hover:bg-gray-50" data-testid={`zoho-log-${log.id}`}>
                        <TableCell className="text-xs text-gray-500 font-mono">
                          {log.id?.slice(0, 8)}...
                        </TableCell>
                        <TableCell className="text-sm font-medium text-gray-900">
                          {log.sampleNo || "-"}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {log.trialNo || "-"}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500 font-mono">
                          {log.recordId || "-"}
                        </TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {formatDate(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          {log.status?.toLowerCase() === "failed" && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-xs text-blue-600"
                              onClick={() => handleRetrySync(log.id)}
                              disabled={retryingLog === log.id}
                            >
                              {retryingLog === log.id ? (
                                <SpinnerGap size={14} className="animate-spin" />
                              ) : (
                                <>
                                  <ArrowClockwise size={14} className="mr-1" />
                                  Retry
                                </>
                              )}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200 mt-6">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <TestTube size={20} className="text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Integration Information</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li><strong>AI Extraction:</strong> Uses GPT-5.2 Vision to extract formulation data from uploaded images</li>
                <li><strong>Zoho Sync:</strong> Updates existing records in Zoho Creator (no duplicates created)</li>
                <li><strong>Auto-Copy:</strong> When creating a new trial (T2+), data is automatically copied from the previous trial</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
